import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { canUseDom } from '@sainsburys-tech/boltui-utils'
import { EmailSkeleton } from './EmailSkeleton'
import DeliveryEmailForm from './DeliveryEmailForm'
import { DeliveryEmailConfirm } from './DeliveryEmailConfirm'
import { pushEventAndTrack } from '../../../../helpers/taggingSerializer'
import { useBrandConfigContext } from '../../../../providers/BrandConfigContext/BrandConfigContext'

import * as Styled from '../DeliveryBox.styles'

const DeliveryEmail = ({
  productId,
  postcode,
  'data-test': dataTest,
  hasSizePicker = false,
  isAvailabilityLoading,
  outOfStockSkus = [],
  isTuMobileLayout
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const { EMAIL_ME_BACK = {} } = useBrandConfigContext()
  const { enabled: brandEnabled, ctaText = 'Notify me', proxyPath: apiUrl, source } = EMAIL_ME_BACK

  const EMAIL_ME_BACK_ENABLED = !canUseDom() || window.EMAIL_ME_BACK_ENABLED

  const SHOW_EMAIL_ME_BACK = EMAIL_ME_BACK_ENABLED && brandEnabled

  if (!SHOW_EMAIL_ME_BACK) return null

  const postData = async data => {
    const url = apiUrl
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
      return response
    } catch (error) {
      return {}
    }
  }

  const onEmailSubmit = (data, sizeSku = '') => {
    const requestedSku = sizeSku || productId
    const body = {
      emailAddress: data,
      productId: requestedSku,
      postcode,
      ...(source && { source }),
      consent: true
    }
    setEmailLoading(true)
    postData(body).then(response => {
      if (response.ok) {
        pushEventAndTrack({
          eventInfo: {
            eventName: 'pdp_emailWhenInStockSubmit',
            eventAction: 'Email When In Stock Submit'
          },
          attributes: {
            productID: requestedSku
          }
        })
      }
      setSubmitSuccess(response.ok)
      setEmailLoading(false)
      setShowConfirmation(true)
    })
  }

  const isLoading = emailLoading || (hasSizePicker && isAvailabilityLoading)

  // Don't show size picker email form if no out of stock skus
  if (!isLoading && hasSizePicker && !outOfStockSkus?.length) {
    return null
  }

  return (
    <Styled.FulfilmentEmailPanel data-test='fulfilment-email-panel'>
      {isLoading && <EmailSkeleton height={hasSizePicker ? '38' : '20'} />}
      {!isLoading &&
        (showConfirmation ? (
          <DeliveryEmailConfirm submitSuccess={submitSuccess} />
        ) : (
          <DeliveryEmailForm
            dataTest={dataTest}
            onEmailSubmit={onEmailSubmit}
            ctaText={ctaText}
            options={outOfStockSkus}
            hasSizePicker={hasSizePicker}
            isTuMobileLayout={isTuMobileLayout}
          />
        ))}
    </Styled.FulfilmentEmailPanel>
  )
}

DeliveryEmail.propTypes = {
  /**
   * Optional prop to provide the postcode for api call (overriden by argument in onEmailSubmit callback function)
   */
  productId: PropTypes.string,
  /**
   * Prop to provide the postcode for api call
   */
  postcode: PropTypes.string,
  /**
   * Prop to enable the out of stock size picker (for Tu clothing)
   */
  hasSizePicker: PropTypes.bool,
  /**
   * Prop to check if the availability data is still loading to retrieve the out of stock skus (for Tu clothing)
   */
  isAvailabilityLoading: PropTypes.bool,
  /**
   * Prop for the list of out of stock skus to display in the size picker version (for Tu clothing)
   * - title: string displayed for the option (sku size) in the select dropdown
   * - value: value of the select option (sku number)
   */
  outOfStockSkus: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.string
    })
  ),
  /**
   * Optional prop to provide data-test for e2e testing
   */
  'data-test': PropTypes.string
}

DeliveryEmail.defaultProps = {
  'data-test': 'fulfillment-delivery-email',
  postcode: ''
}

export default DeliveryEmail
